// @flow

import React, { Component } from 'react';
import ConnectViewer from 'app/common/react/components/common/ConnectViewer/ConnectViewer';
import DenzelEvent from 'app/common/lib/DenzelEvent';
import HistoryNavigator from 'app/common/lib/HistoryNavigator';
import i18n from 'app/common/lib/i18n';
import NavigateData from 'app/common/lib/NavigateData';
import PropTypes from 'prop-types';
import type { Dispatch } from 'redux';
import type { updateUserProps } from 'app/common/redux/actions/nux/updateUser';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import { safeFormat } from 'app/common/lib/StringUtil';
import { track } from 'app/common/lib/react/analytics';
import { updateUser } from 'app/common/redux/actions/nux/updateUser';
import {
  CUSTOM,
  FEMALE,
  MALE,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, Button, Sticky, Text, TextField, Touchable } from 'gestalt';

type HeroProps = {
  // $FlowFixMe: new errors found from flow 0.66 upgrade
  dispatch: Dispatch<*>,
  handleComplete: () => void,
  handleDismiss: () => void,
};

type HeroState = {
  gender: string,
  customGender: string,
  showCustomGenderField: boolean,
};

export default class GenderSignalHero extends Component<HeroProps, HeroState> {
  static contextTypes = {
    userAgent: PropTypes.shape({
      browserName: PropTypes.string.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    handleComplete: () => {},
    handleDismiss: () => {},
  };

  constructor(props: HeroProps) {
    super(props);

    this.state = {
      gender: '',
      customGender: '',
      showCustomGenderField: false,
    };
  }

  onToggleCustomGender = () => {
    this.setState({ showCustomGenderField: !this.state.showCustomGenderField });
  };

  onSelectGender(selectedGender: string) {
    if (selectedGender === CUSTOM && !this.state.showCustomGenderField) {
      this.setState({ gender: '', customGender: '' }, this.onToggleCustomGender);
    } else {
      this.setState({ gender: selectedGender, customGender: '' }, this.updateGenderCompleteHero);
    }
  }

  getCustomGenderField() {
    return (
      <Box alignItems="center" direction="row" display="flex">
        <Box paddingX={1}>
          <TextField
            id="custom_gender_input"
            onChange={({ value }) => this.setState({ customGender: value })}
            placeholder={i18n._('Custom gender', 'Placeholder text for custom gender field')}
            type="text"
          />
        </Box>
        <Box paddingX={1}>
          <Button
            color="white"
            inline
            onClick={() => this.updateGenderCompleteHero()}
            size="md"
            text={i18n._('Done', 'confirm button for entering custom gender')}
          />
        </Box>
      </Box>
    );
  }

  getGenderButtons() {
    const { handleDismiss } = this.props;

    const customGenderOption = this.state.showCustomGenderField ? (
      this.getCustomGenderField()
    ) : (
      <Box paddingX={1}>
        <Button
          color="transparent"
          onClick={() => this.onSelectGender(CUSTOM)}
          size="md"
          text={i18n._('Custom', 'Custom gender option in list of male, female, custom')}
        />
      </Box>
    );

    return (
      <Box alignItems="center" direction="row" display="flex" paddingX={3}>
        <Box minWidth="80px" paddingX={2}>
          <Touchable onTouch={handleDismiss}>
            <Text bold color="white" size="md">
              {i18n._('Not now', 'Dismiss option for gender selection banner on HF')}
            </Text>
          </Touchable>
        </Box>
        <Box paddingX={1}>
          <Button
            color="transparent"
            onClick={() => this.onSelectGender(MALE)}
            size="md"
            text={i18n._('Male', 'Male gender option in list of male, female, custom')}
          />
        </Box>
        <Box paddingX={1}>
          <Button
            color="transparent"
            onClick={() => this.onSelectGender(FEMALE)}
            size="md"
            text={i18n._('Female', 'Female gender option in list of male, female, custom')}
          />
        </Box>
        {customGenderOption}
      </Box>
    );
  }

  getGenderSignalHero() {
    return (
      <Box alignItems="center" direction="row" display="flex" padding={4}>
        <Box flex="grow">
          <Text bold color="white" size="md">
            <ConnectViewer>
              {(viewer: Viewer) => {
                if (viewer.firstName) {
                  return safeFormat(
                    i18n._(
                      'Hey {{first_name}}! Pick what describes you best to personalize your experience on Pinterest',
                      'Copy for banner asking user to select gender',
                    ),
                    { first_name: viewer.firstName },
                  );
                }
                return i18n._(
                  'Hey there! Pick what describes you best to personalize your experience on Pinterest',
                  'Copy for banner asking user to select gender',
                );
              }}
            </ConnectViewer>
          </Text>
        </Box>
        {this.getGenderButtons()}
      </Box>
    );
  }

  updateGenderCompleteHero() {
    const userGenderData: updateUserProps = {
      updateUserData: {
        gender: '',
        custom_gender: '',
      },
    };

    // todo(Sophia) If user entered custom gender, we reset gender field to ''. It isn't clear right now what's the
    // best way to handle non-binary gender. ie. 'unspecified' vs null vs '' gender field.
    if (this.state.showCustomGenderField) {
      userGenderData.updateUserData.custom_gender = this.state.customGender;
      userGenderData.updateUserData.gender = null;
    } else {
      userGenderData.updateUserData.gender = this.state.gender;
      userGenderData.updateUserData.custom_gender = null;
    }

    const genderUpdateCallback = () => {
      this.props.handleComplete();
      // todo(sophia) temporary bad context logging for sake of querying experiment data,
      // will log with dedicated types if ship
      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.BROWSER,
        element: PCONST.thrift.ElementType.GENDER_BUTTON,
        view: PCONST.thrift.ViewType.FEED,
      });

      // refresh page
      const _navigateData = new NavigateData();
      _navigateData.evt = new DenzelEvent('', null, { forceRefresh: 1 });
      HistoryNavigator.navigate('/', _navigateData);
    };

    // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
    this.props.dispatch(updateUser(userGenderData)).then(genderUpdateCallback);
  }

  render() {
    const hero = (
      <div className="Module HeroBasic genderSignalHero gray">{this.getGenderSignalHero()}</div>
    );

    if (this.context.userAgent.browserName === 'IE') {
      return hero;
    } else {
      return (
        <Sticky dangerouslySetZIndex={{ __zIndex: 2 }} top={65}>
          {hero}
        </Sticky>
      );
    }
  }
}

GenderSignalHero.propTypes = {
  dispatch: PropTypes.func.isRequired,
  handleComplete: PropTypes.func,
  handleDismiss: PropTypes.func,
};
